import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useWindow } from '../contexts/system/BrowserAPI/hooks/useWindow.js';
import { createTimer } from './timer.js';
import { useOnMount } from './useOnMount.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getAnimationProps = function getAnimationProps(_ref) {
  var isVisible = _ref.isVisible,
    isExiting = _ref.isExiting,
    _ref$enterProps = _ref.enterProps,
    enterProps = _ref$enterProps === void 0 ? {} : _ref$enterProps,
    _ref$initialProps = _ref.initialProps,
    initialProps = _ref$initialProps === void 0 ? {} : _ref$initialProps,
    _ref$exitProps = _ref.exitProps,
    exitProps = _ref$exitProps === void 0 ? initialProps : _ref$exitProps;
  if (isVisible) {
    return enterProps;
  }
  if (isExiting) {
    return exitProps;
  }
  return initialProps;
};
var reducer = function reducer(state, action) {
  switch (action) {
    case 'animationEnter':
      if (state === 'initial' || state === 'exiting') {
        return 'enteringNotVisible';
      }
      return state;
    case 'timerTrigger':
      if (state === 'enteringNotVisible') {
        return 'enteringVisible';
      }
      return state;
    case 'animationExit':
      if (state === 'visible' || state === 'enteringNotVisible' || state === 'enteringVisible') {
        return 'exiting';
      }
      return state;
    case 'animationFinish':
      if (state === 'exiting') {
        return 'initial';
      }
      if (state === 'enteringVisible') {
        return 'visible';
      }
      return state;
    default:
      return state;
  }
};
var useEnterExitAnimation = function useEnterExitAnimation(_ref2) {
  var transition = _ref2.transition,
    initialProps = _ref2.initialProps,
    exitProps = _ref2.exitProps,
    enterProps = _ref2.enterProps,
    _ref2$animationOn = _ref2.animationOn,
    animationOn = _ref2$animationOn === void 0 ? true : _ref2$animationOn,
    animationFallBackTimeMs = _ref2.animationFallBackTimeMs,
    onAnimationStateChange = _ref2.onAnimationStateChange;
  var timerRef = React.useRef(null);
  var _React$useReducer = React.useReducer(reducer, 'initial'),
    _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
    animationState = _React$useReducer2[0],
    setAnimationState = _React$useReducer2[1];
  var window = useWindow();
  var shouldRender = animationState !== 'initial';
  React.useEffect(function () {
    setAnimationState(animationOn ? 'animationEnter' : 'animationExit');
  }, [animationOn]);
  React.useEffect(function () {
    onAnimationStateChange === null || onAnimationStateChange === void 0 || onAnimationStateChange(animationState);
  }, [onAnimationStateChange, animationState]);
  React.useEffect(function () {
    var id = window.requestAnimationFrame(function () {
      setAnimationState('timerTrigger');
      if (animationFallBackTimeMs !== undefined) {
        timerRef.current = createTimer({
          delay: animationFallBackTimeMs,
          callback: function callback() {
            return setAnimationState('animationFinish');
          }
        });
      }
    });
    return function () {
      var _timerRef$current;
      (_timerRef$current = timerRef.current) === null || _timerRef$current === void 0 || _timerRef$current.destroy();
      window.cancelAnimationFrame(id);
    };
  }, [animationFallBackTimeMs, animationState, window]);
  useOnMount(function () {
    return function () {
      var _timerRef$current2;
      return (_timerRef$current2 = timerRef.current) === null || _timerRef$current2 === void 0 ? void 0 : _timerRef$current2.destroy();
    };
  });
  var onTransitionEnd = function onTransitionEnd(event) {
    var _timerRef$current3;
    if (event.currentTarget !== event.target) {
      return;
    }
    event.stopPropagation();
    (_timerRef$current3 = timerRef.current) === null || _timerRef$current3 === void 0 || _timerRef$current3.destroy();
    setAnimationState('animationFinish');
  };
  var animationProps = getAnimationProps({
    isExiting: animationState === 'exiting',
    isVisible: animationState === 'enteringVisible' || animationState === 'visible',
    enterProps: enterProps,
    exitProps: exitProps,
    initialProps: initialProps
  });
  return {
    transitionProps: _objectSpread(_objectSpread({}, animationProps), {}, {
      transition: transition,
      onTransitionEnd: onTransitionEnd
    }),
    shouldRender: shouldRender
  };
};

export { useEnterExitAnimation };
//# sourceMappingURL=useEnterExitAnimation.js.map
